import React from 'react'
import classes from './ConfigureRoles.module.scss';
import { Button, Form, Modal, Segmented, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import ConfigurePermissions from './components/ConfigurePermissions';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import api from 'api';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setConfigurableRole, setEditingRoleName, setEditNameMode, setGroups, setIsConfigureRolesOpen, setUserAndData } from 'store/slices/dataUsersSlice';
import { fetchUserPermissions } from 'store/reducers/userDataCreator';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import Assets from 'assets/icons';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import MembersOfRole from './components/MembersOfRole';

type ConfigureRolesType = {
  isOpen: boolean;
};

export default function ConfigureRoles({ isOpen }: ConfigureRolesType) {
  const [form] = Form.useForm();
  const [selecetedSegment, setSelecetedSegment] = React.useState('settings');
  const [currentPermissions, setCurrentPermissions] = React.useState<string[]>([]);
  const [isUpdatingRole, setIsUpdatingRole] = React.useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = React.useState(false);
  const [isOpenDeleteGroupModal, setIsOpenDeleteGroupModal] = React.useState(false);
  const [isTheSameFoldersPermissions, setIsTheSameFoldersPermissions] = React.useState(true);  

  const { dataUserTable, users, groups, configurableRole, editingRoleName,
    editNameMode } = useAppSelector((state) => state.dataUsers);
  const { userData, userPermissions } = useAppSelector((state) => state.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const isDefaultGroup = configurableRole && defaultGroups.includes(configurableRole?.name);
  const isAdminRole = configurableRole?.name === 'full_administrator';
  const isTheSameName = configurableRole?.role?.name === editingRoleName;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (editNameMode) setSelecetedSegment('settings');
  }, [editNameMode]);

  React.useEffect(() => {
    // configurableRole?.id === 'new' && form.validateFields();
    dispatch(setEditingRoleName(configurableRole?.name || ''));
    if  (configurableRole?.role?.permissions[0]) {
      const regExp = /'|\[|\]|\n| /g;
      
      const reducePermissions = configurableRole?.role?.permissions.reduce((acc: string[], currnet: string) => {
        const currentPermissions = currnet.replace(regExp, '').split(',');
        return [...acc, ...currentPermissions]
      }, []);
                
      setCurrentPermissions(reducePermissions);
    } else {
      setCurrentPermissions([]);
    }

    return () => {
      dispatch(setEditNameMode(false));
      dispatch(setEditingRoleName(''));
    }
  }, [configurableRole]);

      
  const setModalTitle = (groupId?: string | null) => {
    const titleForOtherRoles = isDefaultGroup
      ? t('Users.modals.configureRoles.defaultRole')
      : t('Users.modals.configureRoles.editRole')
        
    const localizedRoleName = isDefaultGroup
      ? t(`Users.table.${configurableRole?.name}`)
      : configurableRole?.name
    
    switch(groupId) {
      case 'new':
        return t('Users.modals.configureRoles.creatingNewRole');
      case null:
        return t('Users.modals.configureRoles.title');
      default: 
        return <div className={classes.titleWrap}>
          {titleForOtherRoles}
          {localizedRoleName}
          {(!isDefaultGroup && !editNameMode) && (
            <Tooltip title={t('Users.modals.configureRoles.titleEdit')}>
              <span onClick={() => dispatch(setEditNameMode(true))} className={classes.iconWrap}>
                <Assets.EditIcon />
              </span>
            </Tooltip>
          )}
        </div>
    };
  };
  const sendNewPermissions = async () => {    
    setIsUpdatingRole(true);
    const sortedRolePermissions = configurableRole?.role?.permissions && [...configurableRole?.role?.permissions].sort().join(',');
    const isTheSameRolePermissions = [...currentPermissions].sort().join(',') === sortedRolePermissions;
    try {
       if (configurableRole?.role && (!isTheSameRolePermissions || !isTheSameName)) {        
        const updatedRole = await api.updateRole({
          id: configurableRole?.role.id,
          name: editingRoleName,
          permissions: currentPermissions
        });
        
        dispatch(setConfigurableRole({
          ...configurableRole!,
          name: editingRoleName,
          role: updatedRole.data
        }));

        const updatedDataUser = dataUserTable.map(el => el?.role?.id === updatedRole.data.id 
          ? { ...el, name: editingRoleName, role: { ...el.role, permissions: updatedRole.data.permissions, name: editingRoleName, } }
          : el
        );
        
        dispatch(setUserAndData({ dataUserTable: updatedDataUser, users: users }));
        dispatch(setGroups(groups.map(group => group.role?.id === updatedRole.data.id
          ? { ...group, name: editingRoleName, role: { ...group.role, permissions: updatedRole.data.permissions, name: editingRoleName, } }
          : group
        )));

        const myGroupId = users.find(user => user.id === userData?.id)?.group[0];
        if (myGroupId === configurableRole?.group_id) {
          dataRoom && await dispatch(fetchUserPermissions({ idRoom: dataRoom.id }));
        }
        messageApi?.success(t('Users.modals.configureRoles.successUpdate'));
       }
    } catch (e) {
      messageApi?.error(t('Users.modals.configureRoles.errorUpdate'));
    } finally {
      isTheSameFoldersPermissions && setIsUpdatingRole(false);
      dispatch(setEditNameMode(false));
      // setTimeout(() => setIsUpdatingRole(false), 10);
    }
  };

  const disableOkButton = () => {
    const sortedRolePermissions = configurableRole?.role?.permissions && [...configurableRole?.role?.permissions].sort().join(',');
    const isTheSameRolePermissions = [...currentPermissions].sort().join(',') === sortedRolePermissions;

    return (configurableRole?.id === 'new')
      ? false
      : (((isTheSameRolePermissions && isTheSameFoldersPermissions && isTheSameName) || configurableRole?.name === 'full_administrator') || !userPermissions?.can_edit_roles
        ? true
        : false
      )
  };

  const deleteHandle = () => {
    setIsOpenDeleteGroupModal(true);
  };

  const closeDeleteModal = () => setIsOpenDeleteGroupModal(false);

  const onDeleteSubmit = async () => {
    const isExistActiveUsers = configurableRole?.children && configurableRole?.children[0];
    setIsDeletingGroup(true);
    try {
      if (isExistActiveUsers) {
        messageApi?.warning(t(`Users.modals.configureRoles.cannot_delete_role`), 12)
      } else {
        await api.deleteGroup({group_id: configurableRole?.id!, room_id: dataRoom?.id!})
        const filteredGroups = groups.filter(group => group.id !== configurableRole?.id);
        const filteredTableData = dataUserTable.filter(elem => elem.id !== configurableRole?.id);
        dispatch(setUserAndData({
          users: users,
          dataUserTable: filteredTableData,
          groups: filteredGroups,
         }));
  
        messageApi?.success(`
          ${t('Users.modals.configureRoles.successDeleteTextBefore')}
          "${configurableRole?.name}" 
          ${t('Users.modals.configureRoles.successDeleteTextAfter')}
        `);
      }
    } catch (e: any) {
      if (e?.response?.data?.message === 'Нельзя удалить роль — к ней привязаны пользователи' && e?.response?.data?.success === false) {
        messageApi?.error(t('Users.modals.configureRoles.errorDeleteRoleWithUsers'));
      } else {
        messageApi?.error(`
          ${t('Users.modals.configureRoles.errorDeleteTextBefore')}
          "${configurableRole?.name}" 
          ${t('Users.modals.configureRoles.errorDeleteTextAfter')}
        `);
      }
    } finally {
      setIsDeletingGroup(false);
      setIsOpenDeleteGroupModal(false);
      if (!isExistActiveUsers) {
        dispatch(setConfigurableRole(null));
        dispatch(setIsConfigureRolesOpen(false));
      };
    }
  };

  const footerButtons = () => (<div className={classes.footerButtons}>
    {configurableRole?.id !== 'new' && !isDefaultGroup && (
      <Button
        className={classes.delete}
        icon={<Assets.TrashIcon />}
        danger
        onClick={deleteHandle}
      >
        {t('Users.modals.configureRoles.deleteRole')}
      </Button>
    )}

    {(isDefaultGroup || selecetedSegment === 'members') && <Button onClick={() => dispatch(setIsConfigureRolesOpen(false))}>
      {t('Users.modals.configureRoles.close')}
    </Button>}

    {configurableRole?.id !== 'new' && !isAdminRole && selecetedSegment === 'settings' && (
      <Button
        disabled={disableOkButton()}
        type='primary'
        onClick={sendNewPermissions}
      >
        {t('Users.modals.configureRoles.saveChanges')}
      </Button>
    )}

    {configurableRole?.id === 'new' && (
      <Button
        disabled={disableOkButton()}
        type='primary'
        onClick={form.submit}
      >
        {t('Users.modals.configureRoles.create')}
      </Button>
    )}
  </div>);

  const segmentOptions = [
    {
      label: t('Users.modals.configureRoles.settings'),
      value: 'settings'
    },
    {
      label: t('Users.modals.configureRoles.members'),
      value: 'members'
    }
  ];
  
  const displayingContent = {
    settings: <ConfigurePermissions
      form={form}
      setCurrentPermissions={setCurrentPermissions}
      currentPermissions={currentPermissions}
      setIsUpdatingRole={setIsUpdatingRole}
      isUpdatingRole={isUpdatingRole}
      setIsTheSameFoldersPermissions={setIsTheSameFoldersPermissions}
      isTheSameFoldersPermissions={isTheSameFoldersPermissions}
    />,
    members: <MembersOfRole />
  }

  return (
    <Modal
      open={isOpen}
      onCancel={() => !isUpdatingRole && dispatch(setIsConfigureRolesOpen(false))}
      title={setModalTitle(configurableRole?.id || null)}
      confirmLoading={isUpdatingRole}
      className={cn(classes.configureModal, configurableRole?.id !== 'new' ? classes.fixedStyles : 'null', getTheme())}
      centered
      footer={footerButtons}
    >
      <div className={classes.configureWrap}>
        {configurableRole?.id !== 'new' && 
          <Segmented
            className={classes.segmentedElem}
            size='small'
            options={segmentOptions}
            block
            value={selecetedSegment}
            onChange={setSelecetedSegment}
          />
        }
        
        {
          displayingContent[selecetedSegment as keyof typeof displayingContent]
        }
      </div>

      {isUpdatingRole && <UploadingComponent />}

      {isOpenDeleteGroupModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={isOpenDeleteGroupModal}
            onCancel={closeDeleteModal}
            isLoading={isDeletingGroup}
            submit={onDeleteSubmit}
            text={`${t('Users.modals.configureRoles.deleteTextBefore')} "${configurableRole?.name}" 
            ${t('Users.modals.configureRoles.deleteTextAfter')}?`}
            title={t('Users.modals.configureRoles.deleteTitle')}
            centered={true}
          />
        </React.Suspense>
      )}
    </Modal>
  )
}
